export const EXPANDED_MAX_HEIGHT = 375;
export const EXPANDED_SHOW_LESS_SPACE = 48;
export const FIT_TOLERANCE = 1;

export const TRUNCATE_CHECKED_ATTR = 'data-truncate-checked';
export const TRUNCATE_CHILDREN_ATTR = 'data-truncate-children';
export const TRUNCATE_ROW_ATTR = 'data-truncate-row';

export interface OffsetRange {
  bottom: number;
  top: number;
}

export interface Truncation {
  clipHeight: number;
  hiddenCount: number;
}

interface TruncationRowOptions {
  checked?: boolean;
}

// Spread onto a row element so TruncatedCorrelationsList can measure it.
export const truncationRowAttrs = ({ checked = false }: TruncationRowOptions = {}) => ({
  [TRUNCATE_ROW_ATTR]: 'true',
  ...(checked ? { [TRUNCATE_CHECKED_ATTR]: 'true' } : {}),
});

// Spread onto a caret's children container (Collapsible.Content) so children of a visible row are
// never clipped: the collapsed height grows to enclose them.
export const truncationChildrenAttrs = () => ({ [TRUNCATE_CHILDREN_ATTR]: 'true' });

export const bottomRowOffsets = (listContent: HTMLElement, rowAttr: string) => {
  const { top: listTop } = listContent.getBoundingClientRect();

  return Array.from(listContent.querySelectorAll(`[${rowAttr}]`))
    .map(row => row.getBoundingClientRect().bottom - listTop);
};

export const offsetRanges = (listContent: HTMLElement, attr: string): OffsetRange[] => {
  const { top: listTop } = listContent.getBoundingClientRect();

  return Array.from(listContent.querySelectorAll(`[${attr}]`)).map((el) => {
    const { bottom, top } = el.getBoundingClientRect();

    return { bottom: bottom - listTop, top: top - listTop };
  });
};

// Clipped rows stay in the DOM (their heights drive the measurement), so mark them inert to keep
// their controls out of the tab order and the accessibility tree — focusing a hidden control would
// auto-scroll the overflow-hidden clip container. Pass null to clear (expanded / nothing clipped).
export const setClippedRowsInert = (listContent: HTMLElement, clipHeight: number | null) => {
  const { top: listTop } = listContent.getBoundingClientRect();

  listContent.querySelectorAll(`[${TRUNCATE_ROW_ATTR}]`).forEach((row) => {
    const clipped = clipHeight !== null
      && row.getBoundingClientRect().bottom - listTop > clipHeight + FIT_TOLERANCE;
    row.toggleAttribute('inert', clipped);
  });
};

export const computeTruncation = (
  bottomRows: number[],
  childrenRanges: OffsetRange[],
  collapsedMaxHeight: number,
  maxClipHeight: number,
): Truncation | null => {
  const firstHiddenIndex = bottomRows.findIndex(bottom => bottom > collapsedMaxHeight + FIT_TOLERANCE);
  if (firstHiddenIndex === -1) return null;

  const baseClipHeight = bottomRows[firstHiddenIndex - 1] ?? 0;

  // Children of a visible caret row display in full: a children container starting above the clip
  // belongs to a visible row, so the clip grows to its bottom. Nested subtrees are enclosed by
  // their outermost container, so one flat pass covers the whole tree. The growth is capped at
  // maxClipHeight (the viewport-aware expanded cap) so the link below stays reachable — beyond it,
  // re-clip at the last full row inside the cap.
  const extendedClipHeight = Math.max(
    baseClipHeight,
    ...childrenRanges
      .filter(({ top }) => top <= baseClipHeight + FIT_TOLERANCE)
      .map(({ bottom }) => bottom),
  );
  const clipHeight = extendedClipHeight > maxClipHeight + FIT_TOLERANCE
    ? Math.max(
      baseClipHeight,
      ...bottomRows.filter(bottom => bottom <= maxClipHeight + FIT_TOLERANCE),
    )
    : extendedClipHeight;

  const hiddenCount = bottomRows.filter(bottom => bottom > clipHeight + FIT_TOLERANCE).length;

  return hiddenCount === 0 ? null : { clipHeight, hiddenCount };
};
